/*
 * The calendar, and the schedule you build before a meeting.
 *
 * The point of filling this in beforehand is that the app then knows what kind
 * of day it is BEFORE the kart goes out - which is what decides whether it
 * objects to three changes at once or records them quietly - and it knows what
 * is coming next rather than only what already happened.
 *
 * Templates seed a schedule; they are never the schedule. Every meeting runs a
 * different shape, so slots can be added part way through, renamed, reordered
 * and removed.
 */

import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  ArrowDown, ArrowUp, CalendarDays, Plus, Trash2, Unlink,
} from 'lucide-react'
import { ApiError, api } from '../lib/api'
import type { EventSlot, KartEvent, TrackRow } from '../lib/api'
import { eventDates } from '../lib/format'
import { Empty, Spinner } from '../components/bits'

/* -------------------------------------------------------------- the list */
export function Events() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    name: '', track_id: '', event_type: 'club_round',
    start_date: '', end_date: '',
  })

  const events = useQuery({
    queryKey: ['events'],
    queryFn: () => api.get<{ events: KartEvent[] }>('/api/events'),
  })
  const tracks = useQuery({
    queryKey: ['tracks'],
    queryFn: () => api.get<{ tracks: TrackRow[] }>('/api/tracks'),
  })
  const types = useQuery({
    queryKey: ['event-types'],
    queryFn: () => api.get<{ types: { key: string; label: string
      template: { label: string }[] }[] }>('/api/events/types'),
  })

  const create = useMutation({
    mutationFn: () => api.post<KartEvent>('/api/events', {
      name: draft.name,
      track_id: draft.track_id ? Number(draft.track_id) : null,
      event_type: draft.event_type,
      start_date: new Date(draft.start_date).toISOString(),
      end_date: draft.end_date ? new Date(draft.end_date).toISOString() : null,
      apply_template: true,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] })
      setAdding(false); setError(null)
      setDraft({ name: '', track_id: '', event_type: 'club_round',
                 start_date: '', end_date: '' })
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not create.'),
  })

  if (events.isLoading) return <div className="page"><Spinner /></div>
  const rows = events.data?.events ?? []
  const chosen = types.data?.types.find((t) => t.key === draft.event_type)

  const missing = [
    !draft.name.trim() && 'a name',
    !draft.start_date && 'a first day',
  ].filter(Boolean) as string[]

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Calendar</h1>
          <div className="sub">
            Fill a meeting in before you get there. What kind of day it is
            decides how the app treats setup changes.
          </div>
        </div>
        <button className="btn" onClick={() => setAdding((a) => !a)}>
          <Plus size={16} /> Add meeting
        </button>
      </div>

      {adding && (
        <div className="card" style={{ marginBottom: 14 }}>
          <h2 style={{ marginBottom: 12 }}>New meeting</h2>
          <label className="field">
            <span className="name">Name</span>
            <input value={draft.name} placeholder="Toowoomba club round"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label className="field">
            <span className="name">Track</span>
            <select value={draft.track_id}
              onChange={(e) => setDraft({ ...draft, track_id: e.target.value })}>
              <option value=""></option>
              {(tracks.data?.tracks ?? []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="name">Kind of meeting</span>
            <select value={draft.event_type}
              onChange={(e) => setDraft({ ...draft, event_type: e.target.value })}>
              {(types.data?.types ?? []).map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            <span className="hint">
              {chosen?.template.length
                ? `Starts with ${chosen.template.length} sessions: ` +
                  chosen.template.map((s) => s.label).join(', ') +
                  '. Edit them afterwards - every meeting is different.'
                : 'No schedule. Sessions just accumulate as you run them, and ' +
                  'the app objects if more than one thing changes at a time.'}
            </span>
          </label>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <label className="field">
              <span className="name">First day</span>
              <input type="date" value={draft.start_date}
                onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} />
            </label>
            <label className="field">
              <span className="name">Last day</span>
              <input type="date" value={draft.end_date}
                onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} />
              <span className="hint">Leave blank for a single day.</span>
            </label>
          </div>
          {error && <div className="advice warn">{error}</div>}
          {/* A disabled button that says nothing is indistinguishable from a
              broken one - you press it, nothing happens, and there is no way
              to find out why. Say what is missing instead. */}
          {missing.length > 0 && (
            <div className="advice note">
              Still needed: {missing.join(' and ')}.
            </div>
          )}
          <div className="row" style={{ marginTop: 10 }}>
            <button className="btn"
              disabled={missing.length > 0 || create.isPending}
              onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Create meeting'}
            </button>
            <button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <Empty title="Nothing on the calendar"
          body="Add a practice day or a round. Sessions downloaded that day at that track join it on their own." />
      ) : (
        <div className="stack">
          {rows.map((e) => (
            <Link key={e.id} to={`/events/${e.id}`} className="card"
              style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="between wrap">
                <div>
                  <h2>{e.name}</h2>
                  <div className="small muted" style={{ marginTop: 3 }}>
                    {e.track?.name ?? 'No track'} · {eventDates(e.start_date, e.end_date)}
                  </div>
                </div>
                <span className="badge">{e.event_type_label}</span>
              </div>
              {e.slots.length > 0 && (
                <div className="row wrap" style={{ gap: 6, marginTop: 12 }}>
                  {e.slots.map((s) => (
                    <span key={s.id} className={clsx('badge', s.session_id && 'saved')}>
                      {s.label}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------- one meeting */
export function EventDetail() {
  const { id } = useParams()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [newLabel, setNewLabel] = useState('')
  const [newType, setNewType] = useState('heat')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const removeEvent = useMutation({
    mutationFn: () => api.del(`/api/events/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] })
      // Sessions detach rather than disappear, so their lists change too.
      qc.invalidateQueries({ queryKey: ['sessions'] })
      navigate('/events')
    },
    onError: (e: unknown) =>
      setDeleteError(e instanceof ApiError ? e.message : 'Could not delete.'),
  })

  const q = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.get<KartEvent>(`/api/events/${id}`),
  })
  const refresh = () => qc.invalidateQueries({ queryKey: ['event', id] })

  const addSlot = useMutation({
    mutationFn: () => api.post(`/api/events/${id}/slots`,
      { label: newLabel, session_type: newType }),
    onSuccess: () => { setNewLabel(''); refresh() },
  })
  const removeSlot = useMutation({
    mutationFn: (slotId: number) =>
      api.del(`/api/events/${id}/slots/${slotId}`),
    onSuccess: refresh,
  })
  const detach = useMutation({
    mutationFn: (slotId: number) =>
      api.post(`/api/events/${id}/slots/${slotId}/detach`),
    onSuccess: refresh,
  })
  const reorder = useMutation({
    mutationFn: (ids: number[]) =>
      api.post(`/api/events/${id}/slots/reorder`, { slot_ids: ids }),
    onSuccess: refresh,
  })
  const rename = useMutation({
    mutationFn: ({ slotId, label }: { slotId: number; label: string }) =>
      api.patch(`/api/events/${id}/slots/${slotId}`, { label }),
    onSuccess: refresh,
  })

  if (q.isLoading) return <div className="page"><Spinner /></div>
  if (!q.data) return <div className="page"><Empty title="Meeting not found" body="" /></div>
  const e = q.data

  const move = (slots: EventSlot[], index: number, by: number) => {
    const next = [...slots]
    const target = index + by
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target]!, next[index]!]
    reorder.mutate(next.map((s) => s.id))
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{e.name}</h1>
          <div className="sub">
            {e.track?.name ?? 'No track'} · {eventDates(e.start_date, e.end_date)}
            {' · '}{e.event_type_label}
          </div>
        </div>
        <div className="row wrap">
          <Link className="btn ghost" to="/events">
            <CalendarDays size={16} /> All meetings
          </Link>
          {/* Two-step, because a meeting carries its whole schedule and the
              results recorded against it. The sessions themselves are never
              deleted - they detach and keep their telemetry, which outlives
              any plan made about it. */}
          {confirmDelete ? (
            <>
              <span className="small" style={{ color: 'var(--amber)' }}>
                Delete this meeting and its schedule? Sessions are kept.
              </span>
              <button className="btn" style={{ background: 'var(--red)' }}
                disabled={removeEvent.isPending}
                onClick={() => removeEvent.mutate()}>
                {removeEvent.isPending ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button className="btn ghost"
                onClick={() => setConfirmDelete(false)}>Keep it</button>
            </>
          ) : (
            <button className="btn ghost" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={16} /> Delete meeting
            </button>
          )}
        </div>
      </div>

      {deleteError && <div className="advice warn">{deleteError}</div>}

      <div className={clsx('advice', e.is_structured ? 'note' : 'warn')}
        style={{ marginTop: 0, marginBottom: 14 }}>
        {e.is_structured
          ? 'Race weekend: setup changes are recorded without comment. There is one run to fix the kart and no second run to isolate anything in.'
          : 'Practice day: the app will object if more than one thing changes between sessions, because finding out what each change does is the whole reason to be here.'}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Schedule</h2>
          <span className="small dim">
            {e.slots.filter((s) => s.session_id).length} of {e.slots.length} run
          </span>
        </div>

        {e.slots.length === 0 ? (
          <p className="small dim">
            No schedule. Sessions will simply accumulate as they are run.
          </p>
        ) : (
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 34 }}></th>
                  <th>Session</th>
                  <th>Type</th>
                  <th>Ran</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {e.slots.map((s, i) => (
                  <tr key={s.id}>
                    <td className="dim">{i + 1}</td>
                    <td>
                      <input value={s.label} style={{ minWidth: 130 }}
                        onChange={(ev) => rename.mutate(
                          { slotId: s.id, label: ev.target.value })} />
                    </td>
                    <td className="muted">{s.session_type}</td>
                    <td>
                      {s.session_id ? (
                        <span className="row" style={{ gap: 8 }}>
                          <Link className="badge saved"
                            to={`/sessions/${s.session_id}`}>view session</Link>
                          <button className="btn ghost small"
                            title="Unlink this session, keeping it"
                            onClick={() => detach.mutate(s.id)}>
                            <Unlink size={13} />
                          </button>
                        </span>
                      ) : <span className="badge none">not yet</span>}
                    </td>
                    <td className="num">
                      <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                        <button className="btn ghost small" disabled={i === 0}
                          onClick={() => move(e.slots, i, -1)}>
                          <ArrowUp size={13} />
                        </button>
                        <button className="btn ghost small"
                          disabled={i === e.slots.length - 1}
                          onClick={() => move(e.slots, i, 1)}>
                          <ArrowDown size={13} />
                        </button>
                        <button className="btn ghost small"
                          title={s.session_id
                            ? 'Unlink the session first'
                            : 'Remove this session from the schedule'}
                          disabled={!!s.session_id}
                          onClick={() => removeSlot.mutate(s.id)}>
                          <Trash2 size={13} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="row wrap" style={{ gap: 8, marginTop: 14 }}>
          <input value={newLabel} placeholder="Add a session, e.g. Pre-Final"
            style={{ maxWidth: 240 }}
            onChange={(ev) => setNewLabel(ev.target.value)} />
          <select value={newType} style={{ width: 'auto' }}
            onChange={(ev) => setNewType(ev.target.value)}>
            {['practice', 'qualifying', 'heat', 'final', 'test'].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <button className="btn" disabled={!newLabel || addSlot.isPending}
            onClick={() => addSlot.mutate()}>
            <Plus size={16} /> Add
          </button>
        </div>
        <p className="small dim" style={{ marginBottom: 0, marginTop: 12 }}>
          A slot with a session attached cannot be removed by accident — unlink
          it first. The session itself is never deleted either way.
        </p>
      </div>
    </div>
  )
}
